import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { TranslationService } from '../../services/TranslationService';
import { tasksAtom } from './taskAtoms';
import { projectsAtom } from './projectAtoms';
import { Task } from '../types/task';
import { Project } from '../types/project';
import i18n from '../../lib/i18n';

// Define the new nested cache structure
export interface TranslationCache {
  [originalText: string]: {
    [targetLanguage: string]: string;
  };
}

// 翻译服务实例
const translationService = TranslationService.getInstance();

// 翻译状态
export const translationEnabledAtom = atomWithStorage<boolean>('translation_enabled', false);

// 翻译缓存 - Uses the new nested structure
export const translationCacheAtom = atom<TranslationCache>({});

// 加载翻译缓存
export const loadTranslationCacheAtom = atom(
  null,
  (get, set) => {
    try {
      const cached = localStorage.getItem('translation_cache');
      if (cached) {
        const parsedCache: TranslationCache = JSON.parse(cached);
        // Basic validation if needed
        if (typeof parsedCache === 'object' && parsedCache !== null) {
           set(translationCacheAtom, parsedCache);
           console.log('[loadTranslationCache] Loaded cache:', Object.keys(parsedCache).length, 'entries');
        } else {
            console.warn('[loadTranslationCache] Invalid cache format found in localStorage.');
            set(translationCacheAtom, {}); // Reset if invalid
        }
      } else {
         console.log('[loadTranslationCache] No cache found in localStorage.');
      }
    } catch (error) {
      console.error('Failed to load translation cache:', error);
      set(translationCacheAtom, {}); // Reset on error
    }
  }
);

// 保存翻译缓存
export const saveTranslationCacheAtom = atom(
  null,
  (get, set) => {
    try {
      const cache = get(translationCacheAtom);
      // Ensure cache is valid before stringifying
      if (typeof cache === 'object' && cache !== null) { 
        localStorage.setItem('translation_cache', JSON.stringify(cache));
        console.log('[saveTranslationCache] Saved cache:', Object.keys(cache).length, 'entries');
      } else {
        console.warn('[saveTranslationCache] Attempted to save invalid cache structure.');
      }
    } catch (error) {
      console.error('Failed to save translation cache:', error);
    }
  }
);

// 翻译单个文本 (这个可能也需要重新审视或弃用，因为现在主要逻辑在批量部分)
export const translateTextAtom = atom(
  null,
  async (get, set, { text, contentType, id }: { text: string; contentType: string; id: string }) => {
    if (!text) return text;
    const isEnabled = get(translationEnabledAtom);
    if (!isEnabled) return text;
    const targetLang = i18n.language;
    const cache = get(translationCacheAtom);

    // // --- OLD LOGIC using getTranslation - Needs update --- 
    // const { translatedText, newCache } = await translationService.getTranslation(
    //   text,
    //   targetLang,
    //   contentType as any, // ContentType might be irrelevant now if key is just text+lang
    //   id,
    //   cache
    // );
    // set(translationCacheAtom, newCache);
    // set(saveTranslationCacheAtom);
    // return translatedText;
    // --- END OLD LOGIC --- 

    // --- NEW LOGIC (Simpler, direct check/translate) ---
    const cachedTranslation = cache[text]?.[targetLang];
    if (cachedTranslation) {
        return cachedTranslation;
    }
    try {
        console.log(`[translateTextAtom] Cache miss, calling API for: ${text} -> ${targetLang}`);
        const translatedText = await translationService.translateText(text, targetLang);
        // Update cache using set
        set(translationCacheAtom, (prevCache) => ({
            ...prevCache,
            [text]: {
                ...(prevCache[text] || {}),
                [targetLang]: translatedText,
            },
        }));
        set(saveTranslationCacheAtom); // Trigger save
        return translatedText;
    } catch (error) {
        console.error('[translateTextAtom] Translation failed:', error);
        return text; // Return original on error
    }
  }
);

interface TranslationItem {
  text: string;
  contentType: 'task_title' | 'project_title' | 'project_desc' | 'task_desc';
  id: string;
}

interface TranslatedItem extends TranslationItem {
  translatedText: string;
}

// REMOVING batchTranslateAtom as its design was flawed
// // 批量翻译原子
// export const batchTranslateAtom = atom(...);

// --- Helper function for batch translation logic ---
// Helper function REMOVED as logic moves to write-only atom
// async function performBatchTranslation(...) { ... }

// 翻译后的任务列表 - READ-ONLY view based on cache
export const translatedTasksAtom = atom(
  (get) => {
    const tasks = get(tasksAtom);
    const isEnabled = get(translationEnabledAtom);
    console.log('[translatedTasksAtom] Reading. Translation enabled:', isEnabled);
    if (!isEnabled) {
        console.log('[translatedTasksAtom] Disabled, returning original tasks.');
        return tasks;
    }
    const cache = get(translationCacheAtom);
    const language = i18n.language;
    console.log(`[translatedTasksAtom] Cache contains ${Object.keys(cache).length} items for language ${language}.`);
    let titleCacheHits = 0;
    let notesCacheHits = 0;
    const result = tasks.map(task => {
        const cachedTitle = cache[task.title]?.[language];
        const cachedNotes = task.notes ? cache[task.notes]?.[language] : undefined;

        const translatedTask = { ...task };

        if (cachedTitle) {
            titleCacheHits++;
            translatedTask.title = cachedTitle;
        }
        if (cachedNotes) {
            notesCacheHits++;
            translatedTask.notes = cachedNotes;
        }
        
        return translatedTask;
    });
    console.log(`[translatedTasksAtom] Finished mapping tasks. Title hits: ${titleCacheHits}/${tasks.length}, Notes hits: ${notesCacheHits}`);
    return result;
  }
);

// 翻译后的项目列表 - READ-ONLY view based on cache
export const translatedProjectsAtom = atom(
  (get) => {
    const projects = get(projectsAtom);
    const isEnabled = get(translationEnabledAtom);
    console.log('[translatedProjectsAtom] Reading. Translation enabled:', isEnabled);
    if (!isEnabled) {
      console.log('[translatedProjectsAtom] Disabled, returning original projects.');
      return projects;
    }
    const cache = get(translationCacheAtom);
    const language = i18n.language;
    console.log(`[translatedProjectsAtom] Cache contains ${Object.keys(cache).length} items for language ${language}.`);
    let titleCacheHits = 0;
    let contextCacheHits = 0;
    const result = projects.map(project => {
        // Use nested cache access for title
        const cachedTitle = cache[project.title]?.[language];
        // Use nested cache access for context
        const cachedContext = project.context ? cache[project.context]?.[language] : undefined;
        
        const translatedProject = { ...project };
        
        if (cachedTitle) {
            titleCacheHits++;
            translatedProject.title = cachedTitle;
        }
        // Update context only if a translation was found in the cache for the current language
        if (cachedContext) {
             contextCacheHits++;
             translatedProject.context = cachedContext;
        } 
        return translatedProject;
    });
    console.log(`[translatedProjectsAtom] Finished mapping projects. Title hits: ${titleCacheHits}/${projects.length}, Context hits: ${contextCacheHits}`);
    return result;
  }
);

// Atom to trigger translation fetching and cache update (write-only)
export const triggerTranslationUpdateAtom = atom(
  null, // Read function is null for write-only
  async (get, set) => {
    const isEnabled = get(translationEnabledAtom);
    if (!isEnabled) {
      console.log('[triggerTranslationUpdate] Translation disabled, skipping update.');
      return; 
    }
    const language = i18n.language;
    console.log(`[triggerTranslationUpdate] Starting translation update for language: ${language}...`);
    const currentCache = get(translationCacheAtom);
    const tasks = get(tasksAtom); 
    const projects = get(projectsAtom);
    const translationServiceInstance = translationService;

    if (!translationServiceInstance) {
      console.error('[triggerTranslationUpdate] TranslationService instance is missing!');
      return;
    }

    const itemsToTranslate: TranslationItem[] = [];

    // Collect tasks needing translation (title AND notes)
    tasks.forEach(task => {
      // Check title
      if (!currentCache[task.title]?.[language]) {
        itemsToTranslate.push({ text: task.title, contentType: 'task_title', id: task.id });
      }
      // Check notes if they exist
      if (task.notes && !currentCache[task.notes]?.[language]) {
         itemsToTranslate.push({ text: task.notes, contentType: 'task_desc', id: task.id });
      }
    });

    // Collect projects needing translation (title AND context) for the *current* language
    projects.forEach(project => {
      // Check title
      if (!currentCache[project.title]?.[language]) {
        itemsToTranslate.push({ text: project.title, contentType: 'project_title', id: project.id });
      }
      // Check context
      if (project.context && !currentCache[project.context]?.[language]) {
        itemsToTranslate.push({ text: project.context, contentType: 'project_desc', id: project.id });
      }
    });

    if (itemsToTranslate.length === 0) {
      console.log(`[triggerTranslationUpdate] No items need translation for language: ${language}.`);
      return;
    }

    console.log(`[triggerTranslationUpdate] Found ${itemsToTranslate.length} items/fields to translate for language: ${language}.`);
    // No need for local newCache copy, we'll update the atom directly using set's function form

    // Perform translation
    try {
      for (const item of itemsToTranslate) {
        console.log(`[triggerTranslationUpdate] Calling API for [${item.contentType}](${language}): ${item.text}`);
        const translatedText = await translationServiceInstance.translateText(item.text, language);
        // Update the cache atom correctly for the specific language
        set(translationCacheAtom, (prevCache) => ({
          ...prevCache,
          [item.text]: { // Ensure the entry for the original text exists
            ...(prevCache[item.text] || {}), // Keep existing translations for other languages
            [language]: translatedText, // Add/update translation for the current language
          },
        }));
      } 
      
      // After successful translations, trigger save
      console.log('[triggerTranslationUpdate] Translations successful, triggering cache save.');
      set(saveTranslationCacheAtom); 

    } catch (error) {
      console.error('[triggerTranslationUpdate] Translation error during batch:', error);
    }
  }
); 